import SEO, { schemas } from '../components/SEO';
import { Link } from 'react-router-dom';
import { CheckCircle, ArrowRight, Award, Users, Heart, Shield } from 'lucide-react';

const VALUES = [
  { icon: Heart, title: 'Compassionate Service', desc: 'Every passenger is treated with dignity, empathy, and genuine care throughout their journey.' },
  { icon: Shield, title: 'Safety First', desc: 'All vehicles are routinely maintained and sanitized. Drivers are fully licensed, trained, and insured.' },
  { icon: Users, title: 'Community Focused', desc: 'We are proud to serve the Fort Wayne and Indianapolis communities, helping neighbors stay connected to their healthcare.' },
  { icon: Award, title: 'Professional Excellence', desc: 'Our team undergoes rigorous training in patient assistance, mobility support, and first aid.' },
];

const MILESTONES = [
  { year: 'Founded', title: 'MDS Transportation LLC', desc: 'Established with a mission to provide accessible, compassionate NEMT services to underserved communities.' },
  { year: 'Expanded', title: 'Fleet & Services', desc: 'Added wheelchair-accessible vehicles and specialized transport for seniors and hospital discharge patients.' },
  { year: 'Growing', title: 'Community Impact', desc: 'Hundreds of completed rides helping patients stay consistent with their medical care and treatment plans.' },
  { year: 'Today', title: 'Digital Platform', desc: 'Launching our online platform to make booking faster, easier, and more accessible for patients and caregivers.' },
];

export default function AboutPage() {
  return (
    <div>
      <SEO
        title="About Us — Our Mission & Values"
        description="Learn about MDS Transportation LLC — a compassionate NEMT provider committed to safe, reliable, and dignified medical transportation for seniors and patients in Indianapolis and Fort Wayne."
        path="/about"
        schema={schemas.about}
      />
      {/* Hero */}
      <section className="relative py-24 bg-gray-900 overflow-hidden">
        <div className="absolute inset-0">
          <img src="/images/fleet-lineup-wide.jpg" alt="MDS Transportation fleet" className="w-full h-full object-cover opacity-30" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="text-purple-300 font-semibold text-sm uppercase tracking-wider">Our Story</span>
          <h1 className="text-5xl font-bold text-white mt-3 mb-6">About MDS Transportation LLC</h1>
          <p className="text-gray-300 text-lg max-w-2xl mx-auto">A trusted non-emergency medical transportation partner, dedicated to the health, safety, and dignity of every passenger.</p>
        </div>
      </section>

      {/* Mission */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="text-purple-700 font-semibold text-sm uppercase tracking-wider">Who We Are</span>
              <h2 className="text-4xl font-bold text-gray-900 mt-2 mb-6">More Than Just Transportation</h2>
              <p className="text-gray-600 leading-relaxed mb-6">
                At MDS Transportation LLC, we believe that mobility is essential to well-being. As a trusted non-emergency medical transportation (NEMT) provider, we specialize in delivering safe, comfortable, and reliable transportation for individuals who require extra care and assistance while traveling.
              </p>
              <p className="text-gray-600 leading-relaxed mb-6">
                Serving Fort Wayne, Indianapolis and nearby counties, we help seniors, patients, and individuals with mobility challenges reach their destinations with dignity and ease.
              </p>
              <div className="bg-purple-50 border border-purple-100 rounded-2xl p-6">
                <h3 className="font-bold text-purple-900 text-lg mb-3 font-heading">Our Mission</h3>
                {[
                  'To provide stress-free, comfortable, and dependable medical transportation.',
                  'To ensure the safety and well-being of our passengers at all times.',
                  'To help individuals maintain independence by providing accessible travel options.',
                ].map(m => (
                  <div key={m} className="flex items-start gap-3 mb-2">
                    <CheckCircle size={16} className="text-purple-700 mt-0.5 shrink-0" />
                    <span className="text-gray-700 text-sm">{m}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="space-y-4">
              <img src="/images/fleet-silver-row.jpg" alt="MDS Transportation fleet on duty" className="rounded-2xl shadow-lg w-full h-64 object-cover" />
              <div className="grid grid-cols-2 gap-4">
                <img src="/images/van-ramp-navy.jpg" alt="Wheelchair accessible vehicle ramp" className="rounded-2xl shadow w-full h-40 object-cover" />
                <img src="/images/van-interior-ramp2.jpg" alt="Vehicle interior with accessibility features" className="rounded-2xl shadow w-full h-40 object-cover" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <span className="text-purple-700 font-semibold text-sm uppercase tracking-wider">Our Values</span>
            <h2 className="text-4xl font-bold text-gray-900 mt-2">What Drives Us</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {VALUES.map(({ icon: Icon, title, desc }) => (
              <div key={title} className="card text-center border border-gray-100 hover:shadow-lg transition-all">
                <div className="w-14 h-14 bg-purple-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <Icon size={26} className="text-purple-700" />
                </div>
                <h3 className="font-bold text-gray-900 mb-2 font-heading">{title}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Safety commitment */}
      <section className="py-20 bg-purple-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold text-white mb-6 font-heading">Our Commitment to Safety</h2>
              <p className="text-purple-100 text-lg leading-relaxed mb-8">
                At MDS Transportation LLC, your safety is our top priority. Every aspect of our service is designed to protect and care for our passengers.
              </p>
              <div className="space-y-4">
                {[
                  'All drivers are fully licensed, trained, and background-checked',
                  'Vehicles are routinely sanitized and mechanically maintained',
                  'Strict patient assistance and boarding protocols',
                  'Wheelchair tie-downs and safety restraints on all accessible vehicles',
                  'Emergency contact protocols in place for every ride',
                ].map(item => (
                  <div key={item} className="flex items-start gap-3">
                    <CheckCircle size={18} className="text-green-300 mt-0.5 shrink-0" />
                    <span className="text-purple-100">{item}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-3xl p-8 border border-white/20">
              <h3 className="text-2xl font-bold text-white mb-6 font-heading">Milestone Timeline</h3>
              <div className="space-y-6">
                {MILESTONES.map((m, i) => (
                  <div key={i} className="flex gap-4">
                    <div className="w-2 h-2 rounded-full bg-purple-300 mt-2 shrink-0" />
                    <div>
                      <span className="text-purple-300 text-xs font-semibold uppercase tracking-wider">{m.year}</span>
                      <h4 className="font-bold text-white text-sm">{m.title}</h4>
                      <p className="text-purple-200 text-xs leading-relaxed">{m.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-white text-center">
        <div className="max-w-2xl mx-auto px-4">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">Ready to experience the MDS difference?</h2>
          <p className="text-gray-600 mb-8">Book your ride today and discover why hundreds of patients trust us for their medical transportation needs.</p>
          <Link to="/book" className="btn-primary inline-flex items-center gap-2">Book a Ride <ArrowRight size={16} /></Link>
        </div>
      </section>
    </div>
  );
}
